import Bugsnag from "@bugsnag/js";
import { supabase } from "@/lib/supabase";
import { readSupabaseInvokeError } from "@/lib/readSupabaseInvokeError";
import type { LeaderScope } from "@/hooks/useLeaderScope";
import {
  type InviteActionResult,
  type InviteRole,
  type LicenseSummary,
  type PendingInvite,
  type SendInviteInput,
  type SendInviteResult,
} from "@/types/inviteTeammates";
import {
  SAMPLE_DISTRICT_LICENSE_SUMMARY,
  SAMPLE_PENDING_INVITES,
  SAMPLE_SCHOOL_LICENSE_SUMMARY,
} from "@/config/inviteTeammatesSample";
import {
  SAMPLE_DISTRICT_NAME,
  SAMPLE_DISTRICT_SCHOOLS,
  SAMPLE_SCHOOL_TEAM_SNAPSHOT,
} from "@/config/teamsSample";

/**
 * `inviteTeammatesService` — license/pending invite reads, plus the write
 * paths the page mutates (send / resend / cancel).
 *
 * Reads (`getLicenseSummary`, `getPendingInvites`, `getInvitableSchools`) hit
 * `org_licenses` / `pending_invites_with_school` / `schools` and fall back to
 * the sample snapshots in `inviteTeammatesSample.ts` only when those tables
 * are unreachable (e.g. local dev without the AE-195 migration applied).
 *
 * Writes (`sendInvite`, `cancelInvite`, `resendInvite`) always call the
 * `invite-teammate` edge function (AE-196) — there is no sample-store write
 * path. A failed call surfaces the server's error message; unexpected
 * failures (network errors, 5xx) also report to Bugsnag.
 */

const ORG_LICENSES_TABLE = "org_licenses" as const;

interface OrgLicensesRow {
  district_id: string | null;
  school_id: string | null;
  scope_label: string | null;
  leader_total: number | null;
  leader_used: number | null;
  staff_total: number | null;
  staff_used: number | null;
}

interface PendingInviteRow {
  id: string;
  email: string;
  /** Prefer workspace_persona; fall back to app_role-mapped value. */
  workspace_persona?: string | null;
  role: string | null;
  school_id: string | null;
  school_name: string | null;
  status: PendingInvite["status"] | "sent";
  sent_at: string | null;
  created_at?: string | null;
  invited_by_email: string | null;
}

const appRoleToInviteRole = (role: string | null | undefined): PendingInvite["role"] => {
  if (role === "principal" || role === "superintendent" || role === "leader" || role === "district_leader") {
    return "leader";
  }
  if (role === "support_staff" || role === "classified_staff") {
    return "support_staff";
  }
  return "teacher";
};

const normalizeInviteStatus = (
  status: PendingInviteRow["status"]
): PendingInvite["status"] => {
  if (status === "sent") return "pending";
  if (status === "accepted" || status === "expired" || status === "cancelled" || status === "pending") {
    return status;
  }
  return "pending";
};

const sampleStateForScope = (scope: LeaderScope): {
  summary: LicenseSummary;
  invites: PendingInvite[];
} => {
  if (scope.kind === "district" || scope.kind === "super_admin") {
    return {
      summary: { ...SAMPLE_DISTRICT_LICENSE_SUMMARY },
      invites: SAMPLE_PENDING_INVITES.filter((i) => i.status !== "cancelled"),
    };
  }
  return {
    summary: { ...SAMPLE_SCHOOL_LICENSE_SUMMARY },
    invites: SAMPLE_PENDING_INVITES.filter(
      (i) => i.status !== "cancelled" && i.schoolId === SAMPLE_SCHOOL_TEAM_SNAPSHOT.schoolId
    ),
  };
};

/**
 * Live fallback store. Keyed by stable scope so both pages (district roster
 * and a school the leader drilled into) see consistent counts in one session.
 */
const sampleStore = new Map<string, { summary: LicenseSummary; invites: PendingInvite[] }>();

const sampleScopeKey = (scope: LeaderScope): string => {
  switch (scope.kind) {
    case "district":
      return `district:${scope.districtId}`;
    case "school":
      return `school:${scope.schoolId}`;
    case "super_admin":
      return "super_admin";
    case "none":
      return "none";
  }
};

const ensureSampleEntry = (scope: LeaderScope) => {
  const key = sampleScopeKey(scope);
  let entry = sampleStore.get(key);
  if (!entry) {
    const seed = sampleStateForScope(scope);
    entry = {
      summary: { ...seed.summary, pools: { ...seed.summary.pools } },
      invites: seed.invites.map((i) => ({ ...i })),
    };
    sampleStore.set(key, entry);
  }
  return entry;
};

const isMissingTableError = (err: { message?: string } | null): boolean => {
  if (!err?.message) return false;
  return /(org_licenses|pending_invites).+does not exist/i.test(err.message) ||
    /relation .* does not exist/i.test(err.message);
};

const rowToLicenseSummary = (row: OrgLicensesRow, fallbackLabel: string): LicenseSummary => ({
  districtId: row.district_id,
  schoolId: row.school_id,
  scopeLabel: row.scope_label ?? fallbackLabel,
  pools: {
    leader: { total: row.leader_total ?? 0, used: row.leader_used ?? 0 },
    staff: { total: row.staff_total ?? 0, used: row.staff_used ?? 0 },
  },
});

const rowToPendingInvite = (row: PendingInviteRow): PendingInvite => ({
  id: row.id,
  email: row.email,
  role: appRoleToInviteRole(row.workspace_persona ?? row.role),
  schoolId: row.school_id,
  schoolName: row.school_name,
  status: normalizeInviteStatus(row.status),
  sentAt: row.sent_at ?? row.created_at ?? new Date(0).toISOString(),
  invitedByEmail: row.invited_by_email,
});

export async function getLicenseSummary(scope: LeaderScope): Promise<LicenseSummary> {
  if (scope.kind === "none") {
    return ensureSampleEntry(scope).summary;
  }

  try {
    let query = supabase
      .from(ORG_LICENSES_TABLE as never)
      .select(
        "district_id, school_id, scope_label, leader_total, leader_used, staff_total, staff_used"
      );

    if (scope.kind === "school") {
      query = query.eq("school_id", scope.schoolId);
    } else if (scope.kind === "district") {
      query = query.eq("district_id", scope.districtId).is("school_id", null);
    }

    const { data, error } = await query.maybeSingle();

    if (error) {
      if (isMissingTableError(error)) {
        console.info(
          "[inviteTeammatesService] org_licenses missing — falling back to sample license summary."
        );
      } else {
        console.warn("[inviteTeammatesService] license fetch failed:", error.message);
      }
      return ensureSampleEntry(scope).summary;
    }

    if (!data) return ensureSampleEntry(scope).summary;

    const fallbackLabel =
      scope.kind === "district" || scope.kind === "super_admin"
        ? SAMPLE_DISTRICT_NAME
        : SAMPLE_SCHOOL_TEAM_SNAPSHOT.schoolName;
    return rowToLicenseSummary(data as OrgLicensesRow, fallbackLabel);
  } catch (err) {
    console.warn("[inviteTeammatesService] license fetch threw — using sample.", err);
    return ensureSampleEntry(scope).summary;
  }
}

export async function getPendingInvites(scope: LeaderScope): Promise<PendingInvite[]> {
  if (scope.kind === "none") return ensureSampleEntry(scope).invites;

  try {
    // Prefer list view (includes school_name); fall back handled by query error path.
    let query = supabase
      .from("pending_invites_with_school" as never)
      .select(
        "id, email, role, workspace_persona, school_id, school_name, status, sent_at, created_at, invited_by_email"
      )
      .neq("status", "cancelled")
      .order("sent_at", { ascending: false });

    if (scope.kind === "school") {
      query = query.eq("school_id", scope.schoolId);
    } else if (scope.kind === "district") {
      query = query.eq("district_id", scope.districtId);
    }

    const { data, error } = await query;

    if (error) {
      if (isMissingTableError(error)) {
        console.info(
          "[inviteTeammatesService] pending_invites missing — falling back to sample invites."
        );
      } else {
        console.warn("[inviteTeammatesService] invite fetch failed:", error.message);
      }
      return ensureSampleEntry(scope).invites;
    }

    return (data ?? []).map((row) => rowToPendingInvite(row as PendingInviteRow));
  } catch (err) {
    console.warn("[inviteTeammatesService] invite fetch threw — using sample.", err);
    return ensureSampleEntry(scope).invites;
  }
}

const isValidEmail = (raw: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw);

interface InviteEdgeFunctionRow {
  id: string;
  email: string;
  role: InviteRole;
  school_id: string;
  school_name: string | null;
  status: string;
  sent_at: string | null;
  invited_by_email: string | null;
}

interface InviteFunctionSuccess {
  ok: true;
  action: "send" | "resend" | "cancel";
  invite: InviteEdgeFunctionRow;
}

interface InviteFunctionFailure {
  ok: false;
  error: string;
  error_code: string;
}

type InviteFunctionPayload = InviteFunctionSuccess | InviteFunctionFailure;

/** Error codes the edge function returns for expected, user-actionable conditions. */
const EXPECTED_INVITE_ERROR_CODES = new Set<string>([
  "invalid_email",
  "invalid_role",
  "missing_school",
  "missing_invite_id",
  "forbidden",
  "missing_district",
  "school_not_found",
  "school_outside_district",
  "domain_mismatch",
  "domain_not_configured",
  "invalid_domain_email",
  "seat_full",
  "duplicate_pending",
  "invite_not_found",
  "invite_not_pending",
  "already_member",
]);

const notifyUnexpectedInviteError = (context: string, err: unknown) => {
  Bugsnag.notify(err instanceof Error ? err : new Error(String(err)), (event) => {
    event.severity = "error";
    event.context = context;
  });
};

const functionRowToPendingInvite = (row: InviteEdgeFunctionRow): PendingInvite => ({
  id: row.id,
  email: row.email,
  role: row.role,
  schoolId: row.school_id,
  schoolName: row.school_name,
  status: normalizeInviteStatus(row.status as PendingInviteRow["status"]),
  sentAt: row.sent_at ?? new Date().toISOString(),
  invitedByEmail: row.invited_by_email,
});

async function invokeInviteFunction(
  body: Record<string, unknown>,
  context: string
): Promise<{ ok: true; payload: InviteFunctionSuccess } | { ok: false; errorMessage: string }> {
  try {
    const invokeResult = await supabase.functions.invoke("invite-teammate", { body });
    const payload = invokeResult.data as InviteFunctionPayload | null;

    if (payload?.ok) {
      return { ok: true, payload };
    }

    if (payload && !payload.ok) {
      if (!EXPECTED_INVITE_ERROR_CODES.has(payload.error_code)) {
        notifyUnexpectedInviteError(context, new Error(`${payload.error_code}: ${payload.error}`));
      }
      return { ok: false, errorMessage: payload.error };
    }

    const detail = await readSupabaseInvokeError(invokeResult);
    const message = detail ?? invokeResult.error?.message ?? "Could not reach the invite service.";
    notifyUnexpectedInviteError(context, new Error(message));
    return { ok: false, errorMessage: message };
  } catch (err) {
    notifyUnexpectedInviteError(context, err);
    return { ok: false, errorMessage: "Something went wrong. Please try again." };
  }
}

export async function sendInvite(
  _scope: LeaderScope,
  input: SendInviteInput,
  _invitedByEmail: string | null
): Promise<SendInviteResult> {
  const email = input.email.trim().toLowerCase();
  if (!isValidEmail(email)) {
    return { ok: false, errorMessage: "Enter a valid email address." };
  }
  if (!input.schoolId) {
    return { ok: false, errorMessage: "Pick a school for this invite." };
  }

  const result = await invokeInviteFunction(
    { action: "send", email, role: input.role, school_id: input.schoolId },
    "inviteTeammatesService.sendInvite"
  );

  if (!result.ok) {
    return { ok: false, errorMessage: result.errorMessage };
  }

  return { ok: true, invite: functionRowToPendingInvite(result.payload.invite) };
}

export async function cancelInvite(
  _scope: LeaderScope,
  inviteId: string
): Promise<InviteActionResult> {
  const result = await invokeInviteFunction(
    { action: "cancel", invite_id: inviteId },
    "inviteTeammatesService.cancelInvite"
  );
  return result.ok ? { ok: true } : { ok: false, errorMessage: result.errorMessage };
}

export async function resendInvite(
  _scope: LeaderScope,
  inviteId: string
): Promise<InviteActionResult> {
  const result = await invokeInviteFunction(
    { action: "resend", invite_id: inviteId },
    "inviteTeammatesService.resendInvite"
  );
  return result.ok ? { ok: true } : { ok: false, errorMessage: result.errorMessage };
}

/**
 * District domain an invite email must match, resolved from the target
 * school. Used for the "Must use @yourdistrict.org" helper text and
 * client-side pre-validation; the edge function re-checks server-side.
 */
export async function getSchoolDistrictDomain(
  schoolId: string
): Promise<{ districtId: string; domain: string | null } | null> {
  try {
    const { data, error } = await supabase
      .from("schools" as never)
      .select("district_id, districts(domain)")
      .eq("id", schoolId)
      .maybeSingle();

    if (error || !data) return null;

    const row = data as { district_id: string; districts: { domain: string | null } | null };
    return { districtId: row.district_id, domain: row.districts?.domain ?? null };
  } catch (err) {
    console.warn("[inviteTeammatesService] district domain lookup failed", err);
    return null;
  }
}

export interface SchoolPickerOption {
  schoolId: string;
  schoolName: string;
}

/** Schools a district leader can route an invite to (sample fallback when no district data). */
export async function getInvitableSchools(scope: LeaderScope): Promise<SchoolPickerOption[]> {
  if (scope.kind === "school") {
    return [
      { schoolId: scope.schoolId, schoolName: SAMPLE_SCHOOL_TEAM_SNAPSHOT.schoolName },
    ];
  }

  if (scope.kind === "district") {
    try {
      const { data, error } = await supabase
        .from("schools" as never)
        .select("id, name")
        .eq("district_id", scope.districtId)
        .order("name", { ascending: true });

      if (!error && data && data.length > 0) {
        return (data as { id: string; name: string }[]).map((row) => ({
          schoolId: row.id,
          schoolName: row.name,
        }));
      }
    } catch (err) {
      console.warn("[inviteTeammatesService] schools fetch failed — using sample.", err);
    }
    return SAMPLE_DISTRICT_SCHOOLS.map((s) => ({ schoolId: s.schoolId, schoolName: s.schoolName }));
  }

  return SAMPLE_DISTRICT_SCHOOLS.map((s) => ({ schoolId: s.schoolId, schoolName: s.schoolName }));
}
